import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Loader2, CheckCircle2, XCircle, RefreshCw, Inbox, Eye, MailCheck, FileText, Search, ClipboardList, Calendar, History,
} from "lucide-react";

const STATUS_STYLES = {
  pending:  { label: "Pendiente", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  approved: { label: "Aprobada",  cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  rejected: { label: "Cancelada", cls: "bg-red-100 text-red-800 border-red-300" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

// ══════════════════════════════ CONSOLA ══════════════════════════════
const ConsolaTab = () => {
  const [items, setItems] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canEdit, setCanEdit] = useState(false);
  const [status, setStatus] = useState("pending");
  const [empresa, setEmpresa] = useState("all");
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [action, setAction] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchEmpresas = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/formularios-btravel/consola/empresas`);
      setEmpresas(r.data?.items || []);
      setCanEdit(!!r.data?.can_edit);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudieron cargar las empresas");
    }
  }, []);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status });
      if (empresa && empresa !== "all") params.set("empresa", empresa);
      if (q.trim()) params.set("q", q.trim());
      const r = await axios.get(`${API}/formularios-btravel/consola?${params.toString()}`);
      setItems(r.data?.items || []);
      setCanEdit(!!r.data?.can_edit);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo cargar el listado");
    } finally { setLoading(false); }
  }, [status, empresa, q]);

  useEffect(() => { fetchEmpresas(); }, [fetchEmpresas]);
  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openDetail = async (id) => {
    try {
      const r = await axios.get(`${API}/formularios-btravel/consola/${id}`);
      setDetail(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo cargar la solicitud");
    }
  };

  const submitAction = async () => {
    if (!action) return;
    if (action.kind === "cancelar" && !motivo.trim()) {
      toast.error("El motivo es obligatorio para cancelar");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/formularios-btravel/consola/${action.ticket.id}/${action.kind}`, { motivo: motivo.trim() });
      toast.success(action.kind === "aprobar" ? "Solicitud aprobada · ticket enviado a Traveldesk" : "Solicitud cancelada");
      setAction(null); setMotivo("");
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo procesar");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-3 flex flex-wrap items-center gap-2">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-[160px] h-9" data-testid="btcons-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pendientes</SelectItem>
              <SelectItem value="approved">Aprobadas</SelectItem>
              <SelectItem value="rejected">Canceladas</SelectItem>
              <SelectItem value="all">Todas</SelectItem>
            </SelectContent>
          </Select>
          <Select value={empresa} onValueChange={setEmpresa}>
            <SelectTrigger className="w-[220px] h-9" data-testid="btcons-filter-empresa"><SelectValue placeholder="Empresa" /></SelectTrigger>
            <SelectContent className="max-h-64">
              <SelectItem value="all">Empresa: todas</SelectItem>
              {empresas.map((e) => (<SelectItem key={e} value={e}>{e}</SelectItem>))}
            </SelectContent>
          </Select>
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por folio, título o email…" className="pl-8 h-9" data-testid="btcons-filter-q" />
          </div>
          <Button variant="outline" size="sm" onClick={fetchItems} data-testid="btcons-refresh">
            <RefreshCw className="w-4 h-4 mr-1" /> Refrescar
          </Button>
          <span className="text-xs text-slate-500 ml-auto">
            {loading ? "…" : `${items.length} solicitud${items.length === 1 ? "" : "es"}`}
          </span>
        </CardContent>
      </Card>

      {loading ? (
        <div className="py-12 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />Cargando…</div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center text-slate-400"><Inbox className="w-10 h-10 mx-auto mb-2 opacity-40" />Sin solicitudes que coincidan</div>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2 whitespace-nowrap">Folio</th>
                  <th className="text-left px-3 py-2 whitespace-nowrap">Empresa</th>
                  <th className="text-left px-3 py-2 whitespace-nowrap">Solicitante</th>
                  <th className="text-left px-3 py-2 whitespace-nowrap">Título</th>
                  <th className="text-left px-3 py-2 whitespace-nowrap">Estado</th>
                  <th className="text-left px-3 py-2 whitespace-nowrap">Fecha</th>
                  <th className="text-right px-3 py-2 whitespace-nowrap">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((t) => {
                  const st = STATUS_STYLES[t.approval_status] || STATUS_STYLES.pending;
                  return (
                    <tr key={t.id} className="hover:bg-slate-50">
                      <td className="px-3 py-1.5 font-mono text-xs whitespace-nowrap">{t.form_submission_folio || t.folio}</td>
                      <td className="px-3 py-1.5 whitespace-nowrap max-w-[180px] truncate" title={t.cliente}>{t.cliente || "—"}</td>
                      <td className="px-3 py-1.5 whitespace-nowrap max-w-[220px] truncate" title={`${t.created_by_name} · ${t.created_by_email}`}>
                        <div className="text-slate-800">{t.created_by_name || "—"}</div>
                        <div className="text-[11px] text-slate-500 truncate">{t.created_by_email}</div>
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap max-w-[260px] truncate" title={t.title}>{t.title}</td>
                      <td className="px-3 py-1.5 whitespace-nowrap"><Badge className={`border ${st.cls}`}>{st.label}</Badge></td>
                      <td className="px-3 py-1.5 whitespace-nowrap text-xs text-slate-500">{fmtDate(t.created_at)}</td>
                      <td className="px-3 py-1.5 text-right whitespace-nowrap">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openDetail(t.id)} title="Ver detalle">
                          <Eye className="w-4 h-4 text-slate-600" />
                        </Button>
                        {canEdit && t.approval_status === "pending" && (
                          <>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setAction({ kind: "aprobar", ticket: t }); setMotivo(""); }} title="Aprobar">
                              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setAction({ kind: "cancelar", ticket: t }); setMotivo(""); }} title="Cancelar">
                              <XCircle className="w-4 h-4 text-red-600" />
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Detail */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {detail?.ticket?.form_submission_folio || detail?.ticket?.folio}
              <span className="text-sm font-normal text-slate-500 ml-2">{detail?.ticket?.cliente}</span>
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><div className="text-[11px] uppercase font-semibold text-slate-500">Solicitante</div><div>{detail.ticket.created_by_name}</div><div className="text-xs text-slate-500">{detail.ticket.created_by_email}</div></div>
                <div><div className="text-[11px] uppercase font-semibold text-slate-500">Fecha</div><div>{fmtDate(detail.ticket.created_at)}</div></div>
                <div><div className="text-[11px] uppercase font-semibold text-slate-500">Estado</div><Badge className={`border ${(STATUS_STYLES[detail.ticket.approval_status]||STATUS_STYLES.pending).cls}`}>{(STATUS_STYLES[detail.ticket.approval_status]||STATUS_STYLES.pending).label}</Badge></div>
                {detail.ticket.approval_actioned_at && (
                  <div><div className="text-[11px] uppercase font-semibold text-slate-500">Procesada por</div><div>{detail.ticket.approval_actioned_by}</div><div className="text-xs text-slate-500">{fmtDate(detail.ticket.approval_actioned_at)}</div></div>
                )}
                {detail.ticket.approval_motivo && (
                  <div className="col-span-2"><div className="text-[11px] uppercase font-semibold text-slate-500">Motivo/nota</div><div className="italic text-slate-700">{detail.ticket.approval_motivo}</div></div>
                )}
              </div>
              <div className="border rounded p-3 bg-slate-50 text-xs">
                <div className="text-[11px] uppercase font-semibold text-slate-500 mb-1">Descripción</div>
                <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: detail.ticket.description || "" }} />
              </div>
              {(detail.ticket.attachments || []).length > 0 && (
                <div>
                  <div className="text-[11px] uppercase font-semibold text-slate-500 mb-1">Adjuntos</div>
                  <ul className="space-y-1">
                    {(detail.ticket.attachments || []).map((a, i) => (
                      <li key={i} className="text-xs"><a href={a.url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline">{a.filename || a.url}</a></li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Approve/Cancel */}
      <Dialog open={!!action} onOpenChange={(o) => !o && setAction(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{action?.kind === "aprobar" ? "Aprobar solicitud" : "Cancelar solicitud"}</DialogTitle>
          </DialogHeader>
          {action && (
            <div className="space-y-3 text-sm">
              <p><b>{action.ticket.form_submission_folio || action.ticket.folio}</b> · {action.ticket.title}</p>
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase">{action.kind === "aprobar" ? "Nota (opcional)" : "Motivo (obligatorio)"}</label>
                <Textarea rows={3} value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder={action.kind === "aprobar" ? "Ej. Autorizado por presupuesto Q3" : "Ej. Fecha fuera del rango permitido"} />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setAction(null)}>Cerrar</Button>
                <Button onClick={submitAction} disabled={submitting} className={action.kind === "aprobar" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}>
                  {submitting && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                  {action.kind === "aprobar" ? "Aprobar" : "Cancelar solicitud"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ══════════════════════════════ LOG ══════════════════════════════
const LogTab = () => {
  const [items, setItems] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [empresa, setEmpresa] = useState("all");
  const [action, setAction] = useState("all");
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [detail, setDetail] = useState(null);
  const [revertDialog, setRevertDialog] = useState(null); // { ticket }
  const [revertMotivo, setRevertMotivo] = useState("");
  const [reverting, setReverting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/formularios-btravel/consola/empresas`).then((r) => setEmpresas(r.data?.items || [])).catch(() => {});
  }, []);

  const fetchLog = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ action });
      if (empresa !== "all") params.set("empresa", empresa);
      if (q.trim()) params.set("q", q.trim());
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const r = await axios.get(`${API}/formularios-btravel/consola/log?${params.toString()}`);
      setItems(r.data?.items || []);
      setIsAdmin(!!r.data?.is_admin);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo cargar el log");
    } finally { setLoading(false); }
  }, [action, empresa, q, dateFrom, dateTo]);

  useEffect(() => { fetchLog(); }, [fetchLog]);

  const openDetail = async (id) => {
    try {
      const r = await axios.get(`${API}/formularios-btravel/consola/${id}`);
      setDetail(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo cargar el detalle");
    }
  };

  const submitRevert = async () => {
    if (!revertDialog) return;
    if (!revertMotivo.trim()) { toast.error("El motivo es obligatorio"); return; }
    setReverting(true);
    try {
      await axios.post(`${API}/formularios-btravel/consola/${revertDialog.ticket.id}/revertir`, { motivo: revertMotivo.trim() });
      toast.success("Solicitud regresada a estado pendiente");
      setRevertDialog(null);
      setRevertMotivo("");
      setDetail(null);
      fetchLog();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo revertir");
    } finally { setReverting(false); }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-3 flex flex-wrap items-center gap-2">
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger className="w-[160px] h-9" data-testid="btlog-filter-action"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Acción: todas</SelectItem>
              <SelectItem value="approved">Aprobadas</SelectItem>
              <SelectItem value="rejected">Canceladas</SelectItem>
              <SelectItem value="pending">Pendientes</SelectItem>
            </SelectContent>
          </Select>
          <Select value={empresa} onValueChange={setEmpresa}>
            <SelectTrigger className="w-[220px] h-9" data-testid="btlog-filter-empresa"><SelectValue placeholder="Empresa" /></SelectTrigger>
            <SelectContent className="max-h-64">
              <SelectItem value="all">Empresa: todas</SelectItem>
              {empresas.map((e) => (<SelectItem key={e} value={e}>{e}</SelectItem>))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1">
            <Calendar className="w-4 h-4 text-slate-400" />
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-[150px] h-9" data-testid="btlog-filter-from" />
            <span className="text-slate-400 text-sm">→</span>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-[150px] h-9" data-testid="btlog-filter-to" />
          </div>
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Folio, email, motivo o procesador…" className="pl-8 h-9" data-testid="btlog-filter-q" />
          </div>
          <span className="text-xs text-slate-500 ml-auto">
            {loading ? "…" : `${items.length} registro${items.length === 1 ? "" : "s"}`}
          </span>
        </CardContent>
      </Card>

      {loading ? (
        <div className="py-12 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />Cargando…</div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center text-slate-400"><History className="w-10 h-10 mx-auto mb-2 opacity-40" />Sin registros que coincidan</div>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2 whitespace-nowrap">Folio</th>
                  <th className="text-left px-3 py-2 whitespace-nowrap">Empresa</th>
                  <th className="text-left px-3 py-2 whitespace-nowrap">Solicitante</th>
                  <th className="text-left px-3 py-2 whitespace-nowrap">Estado</th>
                  <th className="text-left px-3 py-2 whitespace-nowrap">Procesada por</th>
                  <th className="text-left px-3 py-2 whitespace-nowrap">Fecha acción</th>
                  <th className="text-left px-3 py-2">Motivo</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((t) => {
                  const st = STATUS_STYLES[t.approval_status] || STATUS_STYLES.pending;
                  const wasReverted = (t.reverted_history || []).length > 0;
                  return (
                    <tr key={t.id} className="hover:bg-blue-50 cursor-pointer" onClick={() => openDetail(t.id)} data-testid={`btlog-row-${t.id}`}>
                      <td className="px-3 py-1.5 font-mono text-xs whitespace-nowrap">
                        {t.form_submission_folio || t.folio}
                        {wasReverted && <span className="ml-1 text-[10px] uppercase font-semibold text-amber-600" title="Fue revertida por un admin">⟲</span>}
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap max-w-[180px] truncate" title={t.cliente}>{t.cliente || "—"}</td>
                      <td className="px-3 py-1.5 whitespace-nowrap max-w-[220px] truncate">
                        <div className="text-slate-800">{t.created_by_name || "—"}</div>
                        <div className="text-[11px] text-slate-500 truncate">{t.created_by_email}</div>
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap"><Badge className={`border ${st.cls}`}>{st.label}</Badge></td>
                      <td className="px-3 py-1.5 whitespace-nowrap max-w-[220px] truncate" title={t.approval_actioned_by || ""}>{t.approval_actioned_by || "—"}</td>
                      <td className="px-3 py-1.5 whitespace-nowrap text-xs text-slate-500">{fmtDate(t.approval_actioned_at || t.created_at)}</td>
                      <td className="px-3 py-1.5 max-w-[300px] truncate italic text-slate-700" title={t.approval_motivo || ""}>{t.approval_motivo || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Detail modal (con opción de revertir para admins) */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {detail?.ticket?.form_submission_folio || detail?.ticket?.folio}
              <span className="text-sm font-normal text-slate-500 ml-2">{detail?.ticket?.cliente}</span>
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><div className="text-[11px] uppercase font-semibold text-slate-500">Solicitante</div><div>{detail.ticket.created_by_name}</div><div className="text-xs text-slate-500">{detail.ticket.created_by_email}</div></div>
                <div><div className="text-[11px] uppercase font-semibold text-slate-500">Fecha</div><div>{fmtDate(detail.ticket.created_at)}</div></div>
                <div><div className="text-[11px] uppercase font-semibold text-slate-500">Estado</div><Badge className={`border ${(STATUS_STYLES[detail.ticket.approval_status]||STATUS_STYLES.pending).cls}`}>{(STATUS_STYLES[detail.ticket.approval_status]||STATUS_STYLES.pending).label}</Badge></div>
                {detail.ticket.approval_actioned_at && (
                  <div><div className="text-[11px] uppercase font-semibold text-slate-500">Procesada por</div><div>{detail.ticket.approval_actioned_by}</div><div className="text-xs text-slate-500">{fmtDate(detail.ticket.approval_actioned_at)}</div></div>
                )}
                {detail.ticket.approval_motivo && (
                  <div className="col-span-2"><div className="text-[11px] uppercase font-semibold text-slate-500">Motivo/nota</div><div className="italic text-slate-700">{detail.ticket.approval_motivo}</div></div>
                )}
              </div>

              {/* Historial de reversiones */}
              {(detail.ticket.reverted_history || []).length > 0 && (
                <div className="border border-amber-200 bg-amber-50 rounded p-3">
                  <div className="text-[11px] uppercase font-semibold text-amber-700 mb-1">Historial de reversiones</div>
                  <ul className="space-y-2">
                    {(detail.ticket.reverted_history || []).map((h, i) => (
                      <li key={i} className="text-xs text-slate-700">
                        <span className="font-mono">{fmtDate(h.at)}</span> · <b>{h.admin_email}</b> regresó de <Badge className={`border ${(STATUS_STYLES[h.from_status]||{}).cls || ""}`}>{(STATUS_STYLES[h.from_status]||{}).label || h.from_status}</Badge> a <Badge className="border bg-amber-100 text-amber-800 border-amber-300">Pendiente</Badge>
                        <div className="italic mt-0.5">Motivo: {h.motivo}</div>
                        {h.previous_actioned_by && (
                          <div className="text-[11px] text-slate-500">Antes procesada por {h.previous_actioned_by} · {fmtDate(h.previous_actioned_at)}{h.previous_motivo ? ` · "${h.previous_motivo}"` : ""}</div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
                {detail.is_admin && ["approved", "rejected"].includes(detail.ticket.approval_status) && (
                  <Button
                    variant="outline"
                    onClick={() => { setRevertDialog({ ticket: detail.ticket }); setRevertMotivo(""); }}
                    className="border-amber-400 text-amber-700 hover:bg-amber-50"
                    data-testid="btlog-revert-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-1" /> Regresar a pendiente
                  </Button>
                )}
                <Button variant="outline" onClick={() => setDetail(null)}>Cerrar</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Revert dialog */}
      <Dialog open={!!revertDialog} onOpenChange={(o) => !o && setRevertDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Revertir a estado pendiente</DialogTitle>
          </DialogHeader>
          {revertDialog && (
            <div className="space-y-3 text-sm">
              <p><b>{revertDialog.ticket.form_submission_folio || revertDialog.ticket.folio}</b></p>
              <p className="text-xs text-slate-500">
                Esta solicitud regresará al estado <b>Pendiente</b> y aparecerá nuevamente en la Consola de Solicitudes de la empresa. Quedará registrado en la bitácora.
              </p>
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase">Motivo (obligatorio)</label>
                <Textarea rows={3} value={revertMotivo} onChange={(e) => setRevertMotivo(e.target.value)} placeholder="Ej. Se reabre por solicitud del cliente, error de captura, etc." data-testid="btlog-revert-motivo" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setRevertDialog(null)}>Cancelar</Button>
                <Button onClick={submitRevert} disabled={reverting} className="bg-amber-600 hover:bg-amber-700" data-testid="btlog-revert-confirm">
                  {reverting && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                  Regresar a pendiente
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const FormulariosBTravelConsola = () => {
  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto" data-testid="btravel-consola-page">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <MailCheck className="w-6 h-6 text-indigo-600" /> Formularios BTravel · Consola de Solicitudes
        </h1>
        <p className="text-slate-500 text-sm">Vista interna del personal PD Travel. Consulta todas las consolas de solicitudes y su historial.</p>
      </div>
      <Tabs defaultValue="consola" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="consola" data-testid="btcons-tab-consola">
            <ClipboardList className="w-4 h-4 mr-1" />Consola
          </TabsTrigger>
          <TabsTrigger value="log" data-testid="btcons-tab-log">
            <History className="w-4 h-4 mr-1" />Log histórico
          </TabsTrigger>
        </TabsList>
        <TabsContent value="consola"><ConsolaTab /></TabsContent>
        <TabsContent value="log"><LogTab /></TabsContent>
      </Tabs>
    </div>
  );
};

export default FormulariosBTravelConsola;
